import { useToast } from '@lifebit-ai/lds'
import { UseMutationOptions, useMutation } from '@tanstack/react-query'
import { DownloadFileMethods } from 'enums'

import { getFileContent } from 'services/results'
import { ApiErrorResponse } from 'services/types'
import { download, downloadFromSignedUrl } from 'utils/download'

interface UseDownloadFileArgs {
  resultId: string
  bucket: string
  fileKey: string
  fileName: string
  method: DownloadFileMethods
}

/** mutation-cache: read-only, it only fetches a signed URL to download a file and changes no server state */
export const useDownloadFile = (options?: UseMutationOptions<void, ApiErrorResponse, UseDownloadFileArgs>) => {
  const toast = useToast()
  const { onSuccess, onError, ...restOptions } = options ?? {}

  return useMutation<void, ApiErrorResponse, UseDownloadFileArgs>({
    mutationFn: async ({ resultId, bucket, fileKey, fileName, method }) => {
      const { signedUrl } = await getFileContent({
        runId: resultId,
        bucket,
        fileKey,
        fetchSignedUrlOnly: true
      })

      if (method === DownloadFileMethods.DownloadInTabWithWindowOpen) {
        window.open(signedUrl, '_blank')
        return
      }

      if (method === DownloadFileMethods.DownloadInTabWithHiddenLink) {
        download(signedUrl, fileName, '_blank')
        return
      }

      await downloadFromSignedUrl(signedUrl, fileName)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      toast({ message: 'Download completed', type: 'success' })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        message: 'Your download failed. If this issue persists, please contact your administrator.',
        type: 'error'
      })
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions
  })
}
